import type { Query, QueryKey } from "@tanstack/react-query";
import { hashKey } from "@tanstack/react-query";

// Allowlist of query keys whose data should be dehydrated into the persisted
// cache. Empty by default — nothing is persisted until a dataset is prepared
// (Phase 7). Phase 7's prepareDataset adds keys here; Phase 9's decommission
// of Workbox relies on this being the sole persistence gate.
const allowedHashes = new Set<string>();

export function addToDehydrateAllowlist(queryKey: QueryKey): void {
	allowedHashes.add(hashKey(queryKey));
}

export function removeFromDehydrateAllowlist(queryKey: QueryKey): void {
	allowedHashes.delete(hashKey(queryKey));
}

export function _resetForTesting(): void {
	allowedHashes.clear();
}

/**
 * Query-key roots that must NEVER be restored from disk.
 *
 * Assistant proposals are a live review surface: a persisted card can come back
 * after a reload showing PENDING with an active Approve for a proposal that has
 * already executed, or hiding a kill switch flipped since the snapshot was
 * written — the card's `version` (the consent token) does not move on either
 * transition, so a restored copy can tie with, and win over, the live one. The
 * server refuses every stale mutation, so nothing wrong is ever WRITTEN; what
 * breaks is the one thing the card exists to get right — reporting state.
 * Offline value here is nil: a proposal cannot be reviewed offline anyway.
 *
 * Weather is the same class for a different reason. The persisted cache has a
 * 5-day `maxAge`, so a restored reading would paint as *current* — a farmer
 * reading Monday's forecast on Friday and spraying on it. The reading is also
 * fetched live from a third-party chain on every load and is never stored
 * server-side, so there is nothing to reconcile against; and Google's terms
 * (plan §GW-2) restrict caching and storage of its Content anyway. Offline
 * value is nil: a forecast cannot be fetched without a network.
 *
 * Import history decides whether the import page offers an upload or blocks it
 * behind the imports still open. A restored copy brings back imports already
 * discarded or finished, blocking the page until the next refetch. The import
 * pages are online-only, so offline value is nil.
 */
const NEVER_DEHYDRATE_ROOTS: readonly string[] = [
	"assistant-proposals",
	"weather",
	"import-history",
];

export function shouldDehydrateQuery(query: Query): boolean {
	// The block wins over the allowlist: a live review surface stays live even if
	// something later prepares it as an offline dataset by mistake.
	if (isNeverDehydrated(query.queryKey)) return false;
	// Always dehydrate explicitly prepared offline datasets (ensures fresh animal
	// data is persisted even before the query goes stale). Only once they hold
	// data: a query with none is pending, and dehydrating it writes its in-flight
	// promise, which IndexedDB cannot clone, so every cache save would fail.
	if (
		allowedHashes.has(hashKey(query.queryKey)) &&
		query.state.data !== undefined
	)
		return true;
	// Also dehydrate all successful queries so that the app shell (org context,
	// access profile, establishments, etc.) survives an offline F5 after the
	// user has prepared their data (spec AC-F7). These are small context queries;
	// the budget is controlled by PersistQueryClientProvider's maxAge + buster.
	return query.state.status === "success";
}

/**
 * Matched on the ROUTE of any segment — the generated keys are
 * `[{ url, params }, …]`, so the resource name lives inside the leading object,
 * not in a plain string segment.
 */
function isNeverDehydrated(queryKey: QueryKey): boolean {
	return queryKey.some((segment) => {
		const url =
			typeof segment === "string"
				? segment
				: (segment as { url?: unknown } | null)?.url;
		return (
			typeof url === "string" &&
			NEVER_DEHYDRATE_ROOTS.some((root) => url.includes(root))
		);
	});
}
